import type { UseFormSetValue } from "react-hook-form";
import { boolean, object, string } from "yup";
import type { Deal, DealType, InspectionBy, WitnessType } from "~/models/Deal";
import type { Property, PropertyType } from "~/models/kensaku_kun/property";
import { buildPropertyByDeal } from "~/models/kensaku_kun/property";
import type { PropertyRoomOption } from "~/models/PropertyRoomOption";
import type { Staff } from "~/models/Staff";
import type { Subtype } from "~/models/Subtype";
import type { CompanyVendor } from "./CompanyVendor";
import type { DealFormRepairParam } from "./DealRepair";
import type { PropertyDealBuilding, PropertyDealRoom } from "./PropertyDeal";
import { VENDOR_COMPANY_ITANDI_ID } from "./VendorCompany";

export type DealParam = {
	address: string | null;
	amaterasBuildingPropertyId: string | null; // idのみで良いかもしれない
	companyVendor: CompanyVendor | null;
	conductFieldSurvey: "true" | "false" | null;
	dealRepair: DealFormRepairParam | null;
	dealSubtype: Subtype | null;
	dealType: "restoration" | "repair" | "other" | null;
	enableAutoLock: "true" | "false" | null;
	enableElevator: "true" | "false" | null;
	estimateAddressName: string;
	estimationByCompany: boolean;
	floorMap: File | null;
	freeParking: string;
	inspectionBy: InspectionBy;
	inspectionCandidateDate1: string | null;
	inspectionCandidateDate2: string | null;
	inspectionCandidateDate3: string | null;
	inspectionCandidateDateTime1: string | null;
	inspectionCandidateDateTime2: string | null;
	inspectionCandidateDateTime3: string | null;
	inspectionScheduleAdjustmentNote: string | null;
	isRegisterAsBuilding: boolean;
	leaseContractFile1: File | null;
	leaseContractFile2: File | null;
	leaseContractFile3: File | null;
	leasingId: string | null;
	leasingOriginStartDate: string | null;
	note: string;
	placeKeyStoring: string;
	preferredDate: string | null;
	property: Property<PropertyType> | null; // idのみで良いかもしれない
	roomName: string | null;
	roomNumber: string | null;
	// idのみで良いかもしれない
	showTenant: boolean;
	staff: Staff | null;
	tenantEmail: string | null;
	tenantName: string | null;
	tenantNameKana: string | null;
	tenantTel: string | null;
	terminationDate: string | null;
	witnessEmail: string | null;
	witnessName: string | null;
	witnessNameKana: string | null;
	witnessTel: string | null;
	witnessType: WitnessType;
};

export type RelatedDealParam = Omit<
	DealParam,
	| "property"
	| "isRegisterAsBuilding"
	| "roomName"
	| "roomNumber"
	| "address"
	| "enableAutoLock"
	| "enableElevator"
	| "placeKeyStoring"
	| "freeParking"
	| "floorMap"
	| "leasingId"
	| "preferredDate"
	| "leaseContractFile1"
	| "leaseContractFile2"
	| "leaseContractFile3"
	| "leasingOriginStartDate"
	| "amaterasBuildingPropertyId"
>;

export const getNextWitnessType = ({
	dealType,
	inspectionBy,
	currentWitnessType
}: {
	currentWitnessType: WitnessType;
	dealType: DealType | null;
	inspectionBy: InspectionBy;
}): WitnessType => {
	if (dealType === "restoration" && inspectionBy === "none") {
		return "none";
	}

	if (dealType === "restoration" && currentWitnessType === "none") {
		return "tenant";
	}

	return currentWitnessType;
};

export const buildDealParam = ({
	inspectionBy,
	dealType,
	leasingId
}: {
	dealType: DealType | null;
	inspectionBy: InspectionBy;
	leasingId: string | null;
}): DealParam => ({
	address: "",
	amaterasBuildingPropertyId: null,
	companyVendor: null,
	conductFieldSurvey: null,
	dealRepair: {
		itemCategory: "",
		itemName: "",
		manufacturer: "",
		modelNumber: "",
		modelNumberFile: null,
		requestContent: ""
	},
	dealSubtype: null,
	dealType,
	enableAutoLock: null,
	enableElevator: null,
	estimateAddressName: "",
	estimationByCompany: false,
	floorMap: null,
	freeParking: "",
	inspectionBy,
	inspectionCandidateDate1: null,
	inspectionCandidateDate2: null,
	inspectionCandidateDate3: null,
	inspectionCandidateDateTime1: "",
	inspectionCandidateDateTime2: "",
	inspectionCandidateDateTime3: "",
	inspectionScheduleAdjustmentNote: "",
	isRegisterAsBuilding: false,
	leaseContractFile1: null,
	leaseContractFile2: null,
	leaseContractFile3: null,
	leasingId,
	leasingOriginStartDate: null,
	note: "",
	placeKeyStoring: "",
	preferredDate: null,
	property: null,
	roomName: "",
	roomNumber: "",
	showTenant: true,
	staff: null,
	tenantEmail: "",
	tenantName: "",
	tenantNameKana: "",
	tenantTel: "",
	terminationDate: null,
	witnessEmail: "",
	witnessName: "",
	witnessNameKana: "",
	witnessTel: "",
	witnessType: "tenant"
});

export type DealUpdateParam = Omit<
	DealParam,
	| "dealSubtype"
	| "floorMap"
	| "leaseContractFile1"
	| "leaseContractFile2"
	| "leaseContractFile3"
	| "dealRepair"
	| "conductFieldSurvey"
	| "inspectionCandidateDate1"
	| "inspectionCandidateDateTime1"
	| "inspectionCandidateDate2"
	| "inspectionCandidateDateTime2"
	| "inspectionCandidateDate3"
	| "inspectionCandidateDateTime3"
	| "companyVendor"
	| "showTenant"
	| "estimationByCompany"
	| "leasingId"
> & {
	amaterasPropertyId: number | null;
	name: string | null;
};

export const buildDealUpdateParam = (deal: Deal): DealUpdateParam => ({
	address: deal.address,
	amaterasBuildingPropertyId: deal.buildingPropertyId
		? String(deal.buildingPropertyId)
		: null,
	amaterasPropertyId: deal.propertyId,
	dealType: deal.dealType,
	enableAutoLock: deal.enableAutoLock ? "true" : "false",
	enableElevator: deal.enableElevator ? "true" : "false",
	estimateAddressName: deal.estimateAddressName,
	freeParking: deal.freeParking,
	inspectionBy: deal.inspectionBy,
	inspectionScheduleAdjustmentNote: deal.inspectionScheduleAdjustmentNote,
	isRegisterAsBuilding: deal.isRegisterAsBuilding,
	leasingOriginStartDate: deal.leasingOriginStartDate,
	name: deal.name,
	note: deal.note,
	placeKeyStoring: deal.placeKeyStoring,
	preferredDate: deal.preferredDate,
	property: buildPropertyByDeal(deal),
	roomName: deal.name,
	roomNumber: deal.roomNumber ?? "",
	staff: deal.staff,
	tenantEmail: deal.tenantEmail,
	tenantName: deal.tenantName,
	tenantNameKana: deal.tenantNameKana,
	tenantTel: deal.tenantTel,
	terminationDate: deal.terminationDate,
	witnessEmail: deal.witnessEmail,
	witnessName: deal.witnessName,
	witnessNameKana: deal.witnessNameKana,
	witnessTel: deal.witnessTel,
	witnessType: getNextWitnessType({
		currentWitnessType: deal.witnessType,
		dealType: deal.dealType,
		inspectionBy: deal.inspectionBy
	})
});

export const resetBuildingValues = (
	setValue: UseFormSetValue<DealParam | DealUpdateParam>
): void => {
	setValue("placeKeyStoring", "");
	setValue("freeParking", "");
	setValue("enableElevator", null);
	setValue("enableAutoLock", null);
};
export const resetTenantValues = (
	setValue: UseFormSetValue<DealParam>
): void => {
	setValue("tenantName", "");
	setValue("tenantNameKana", "");
	setValue("tenantTel", "");
	setValue("tenantEmail", "");
};

export const setBuildingValues = (
	setValue: UseFormSetValue<DealParam | DealUpdateParam>,
	propertyDeal: PropertyDealRoom | PropertyDealBuilding
): void => {
	setValue("placeKeyStoring", propertyDeal.placeKeyStoring);
	setValue("freeParking", propertyDeal.freeParking);
	setValue("enableElevator", propertyDeal.enableElevator ? "true" : "false");
	setValue("enableAutoLock", propertyDeal.enableAutoLock ? "true" : "false");
};

export const setTenantValues = (
	setValue: UseFormSetValue<DealParam>,
	propertyRoomOption: PropertyDealRoom | PropertyRoomOption // TODO: NSKDEV-1866でDealEditorBodyV2に切り替え後PropertyDealRoomは消す
): void => {
	setValue("tenantName", propertyRoomOption.tenantName ?? "");
	setValue("tenantNameKana", propertyRoomOption.tenantNameKana ?? "");
	setValue("tenantTel", propertyRoomOption.tenantTel ?? "");
	setValue("tenantEmail", propertyRoomOption.tenantEmail ?? "");
};

export const requestContentPlaceholder =
	"例）\nいつから （日付）:5/30（入居時）\nどこで （場所）：キッチン\n何を（物） ：タオル掛け\nなぜ （経緯）：入居時から壊れてた\nどのように（状況）:接着剤で止めた跡があるが、片方に亀裂が入りすでに割れてる。もう一方でずっと支えてる形になってるのでいつ壊れてもおかしくない";

type WitnessFields = Pick<
	DealParam,
	| "witnessEmail"
	| "inspectionBy"
	| "inspectionScheduleAdjustmentNote"
	| "witnessName"
	| "witnessNameKana"
	| "witnessTel"
	| "witnessType"
>;

export const normalizeFields = <T extends WitnessFields>(value: T): T => {
	const inspectionScheduleAdjustmentNote =
		value.witnessType === "none" ? "" : value.inspectionScheduleAdjustmentNote;

	if (value.witnessType === "specified") {
		return {
			...value,
			inspectionScheduleAdjustmentNote
		};
	}

	return {
		...value,
		inspectionScheduleAdjustmentNote,
		witnessEmail: null,
		witnessName: null,
		witnessNameKana: null,
		witnessTel: null
	};
};

export const isTenantRequired = ({
	dealType,
	inspectionBy,
	witnessType
}: {
	dealType: DealType | null;
	inspectionBy: InspectionBy;
	witnessType?: WitnessType;
}): boolean => {
	if (inspectionBy !== "vendor") return false;
	if (dealType !== "restoration") return false;
	return witnessType === "tenant";
};

export const isWitnessRequired = ({
	dealType,
	inspectionBy,
	witnessType
}: {
	dealType: DealType | null;
	inspectionBy: InspectionBy;
	witnessType: WitnessType;
}): boolean =>
	dealType === "restoration" &&
	inspectionBy === "vendor" &&
	witnessType === "specified";

const phoneNumberSchema = string()
	.nullable()
	.matches(/^([-0-9]*)?$/, {
		message: "電話番号は数字とハイフンのみで入力してください"
	})
	.matches(/^(0[0-9]+-?[0-9]+-?[0-9]+)?$/, {
		message: "電話番号の形式を確認してください"
	})
	.test(
		"PhoneNumber",
		"電話番号は10桁または11桁で入力してください(-は数えません)",
		(val: string | undefined | null) =>
			[0, 10, 11].includes((val ?? "").replace(/-/g, "").length)
	);

const tenantNameSchema = string()
	.nullable()
	.when(["dealType", "inspectionBy", "witnessType"], (values, schema) =>
		isTenantRequired({
			dealType: values[0],
			inspectionBy: values[1],
			witnessType: values[2]
		})
			? schema.required("入力してください")
			: schema
	);

const tenantNameKanaSchema = string()
	.nullable()
	.when(["dealType", "inspectionBy", "witnessType"], (values, schema) =>
		isTenantRequired({
			dealType: values[0],
			inspectionBy: values[1],
			witnessType: values[2]
		})
			? schema.required("入力してください")
			: schema
	);

const tenantEmailSchema = string()
	.nullable()
	.email("メールアドレスの形式を確認してください");

const tenantTelSchema = phoneNumberSchema.when(
	["dealType", "inspectionBy", "witnessType"],
	(values, schema) =>
		isTenantRequired({
			dealType: values[0],
			inspectionBy: values[1],
			witnessType: values[2]
		})
			? schema.required("入力してください")
			: schema
);

const witnessNameSchema = string()
	.nullable()
	.when(["dealType", "inspectionBy", "witnessType"], (values, schema) =>
		isWitnessRequired({
			dealType: values[0],
			inspectionBy: values[1],
			witnessType: values[2]
		})
			? schema.required("入力してください")
			: schema
	);

const witnessNameKanaSchema = string()
	.nullable()
	.when(["dealType", "inspectionBy", "witnessType"], (values, schema) =>
		isWitnessRequired({
			dealType: values[0],
			inspectionBy: values[1],
			witnessType: values[2]
		})
			? schema.required("入力してください")
			: schema
	);

const witnessEmailSchema = string()
	.nullable()
	.when(["witnessType"], ([witnessType], schema) =>
		witnessType === "specified"
			? schema.email("メールアドレスの形式を確認してください")
			: schema
	);

const witnessTelSchema = string()
	.nullable()
	.when(
		["dealType", "inspectionBy", "witnessType"],
		([dealType, inspectionBy, witnessType], schema) =>
			witnessType === "specified"
				? isWitnessRequired({
						dealType,
						inspectionBy,
						witnessType
					})
					? phoneNumberSchema.required("入力してください")
					: phoneNumberSchema
				: schema
	);

export const validationSchemaV2 = object().shape({
	address: string().required("入力してください"),
	companyVendor: object().nullable().required("入力してください"),
	conductFieldSurvey: boolean()
		.nullable()
		.when("dealType", {
			is: "repair",
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) => schema.required("入力してください")
		}),
	dealRepair: object()
		.nullable()
		.when("dealType", {
			is: "repair",
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) =>
				schema.shape({
					itemCategory: string().required("入力してください").nullable(),
					requestContent: string().required("入力してください").nullable()
				})
		}),
	dealSubtype: object()
		.nullable()
		.when("dealType", {
			is: "other",
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) => schema.required("入力してください")
		}),
	dealType: string().nullable().required("入力してください"),
	enableAutoLock: boolean().nullable().required("入力してください"),
	enableElevator: boolean().nullable().required("入力してください"),
	estimateAddressName: string().required("入力してください"),
	inspectionBy: string().required("入力してください"),
	inspectionScheduleAdjustmentNote: string()
		.nullable()
		.when("witnessType", {
			is: "none",
			otherwise: (schema) =>
				schema.max(
					1_000,
					"日程調整時の注意事項は1,000文字以内で入力してください"
				),
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) => schema.transform(() => "")
		}),
	isRegisterAsBuilding: boolean().required("入力してください"),
	roomName: string().required("入力してください"),
	roomNumber: string().when("isRegisterAsBuilding", {
		is: false,
		// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
		then: (schema) => schema.required("入力してください")
	}),
	staff: object().nullable().required("入力してください"),
	tenantEmail: tenantEmailSchema,
	tenantName: tenantNameSchema,
	tenantNameKana: tenantNameKanaSchema,
	tenantTel: tenantTelSchema,
	witnessEmail: witnessEmailSchema,
	witnessName: witnessNameSchema,
	witnessNameKana: witnessNameKanaSchema,
	witnessTel: witnessTelSchema,
	witnessType: string()
		.oneOf(["tenant", "specified", "none"], "入力してください")
		.required("入力してください")
});

export const updateValidationSchemaV2 = object().shape({
	address: string().required("入力してください"),
	enableAutoLock: boolean().nullable().required("入力してください"),
	enableElevator: boolean().nullable().required("入力してください"),
	estimateAddressName: string().required("入力してください"),
	inspectionBy: string().required("入力してください"),
	inspectionScheduleAdjustmentNote: string()
		.nullable()
		.when("witnessType", {
			is: "none",
			otherwise: (schema) =>
				schema.max(
					1_000,
					"日程調整時の注意事項は1,000文字以内で入力してください"
				),
			// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
			then: (schema) => schema.transform(() => "")
		}),
	isRegisterAsBuilding: boolean().required("入力してください"),
	roomName: string().required("入力してください"),
	roomNumber: string().when("isRegisterAsBuilding", {
		is: false,
		// biome-ignore lint/suspicious/noThenProperty: Yupのwhen/then構文のため
		then: (schema) => schema.required("入力してください")
	}),
	staff: object().nullable().required("入力してください"),
	tenantEmail: tenantEmailSchema,
	tenantName: tenantNameSchema,
	tenantNameKana: tenantNameKanaSchema,
	tenantTel: tenantTelSchema,
	witnessEmail: witnessEmailSchema,
	witnessName: witnessNameSchema,
	witnessNameKana: witnessNameKanaSchema,
	witnessTel: witnessTelSchema,
	witnessType: string()
		.oneOf(["tenant", "specified", "none"], "入力してください")
		.required("入力してください")
});

export const relatedDealValidationSchema = validationSchemaV2.omit([
	"isRegisterAsBuilding",
	"roomName",
	"roomNumber",
	"address",
	"enableAutoLock",
	"enableElevator"
]);

export const shouldConfirmShowTenant = ({
	companyVendor,
	showTenant
}: {
	companyVendor: CompanyVendor | null;
	showTenant: boolean;
}): boolean =>
	companyVendor !== null &&
	companyVendor.vendorCompany?.id !== VENDOR_COMPANY_ITANDI_ID &&
	showTenant;
